import React from 'react';
import { createPortal } from 'react-dom';
import { GuideBotOverlay, CommerceHubModals } from '..';
import { DashboardModalsSection } from './DashboardModalsSection';
import { ActiveDesignBadge } from './ActiveDesignBadge';
import type { SavedAIDesign } from '../../../../../store/oneButtonStore';
import type { DashboardSectionPanelId } from '../dashboardSectionNav';
import {
    OBD_PRICING_GRID_MODAL_Z,
    OBD_PRICING_GRID_NESTED_MODAL_Z,
} from '../oneButtonOverlayZ';

interface DashboardOverlayExtrasSectionProps {
    initialDashboardReady: boolean;
    isDashboardOpen: boolean;
    /** Opens Sunny / opportunity assistant (viewport-bottom dock, not section rail). */
    onOpenAssistant?: () => void;
    opportunityAssistantOpen: boolean;
    setOpportunityAssistantOpen: (next: boolean) => void;
    pricingGridModalOpen: boolean;
    setPricingGridModalOpen: (next: boolean) => void;
    theme: string;
    pricingFetchTrigger: number | null;
    pricingDesignForTrigger: any;
    priceQuote: any;
    setPricingFetchTrigger: React.Dispatch<React.SetStateAction<number | null>>;
    ahjModalOpen: boolean;
    setAhjModalOpen: (next: boolean) => void;
    ahjTown: string;
    setbackNotes: string[];
    addTitleContactsModalOpen: boolean;
    setAddTitleContactsModalOpen: (next: boolean) => void;
    titleSearchData: any;
    handleTitleSyncSuccess: (...args: any[]) => void;
    blackKnightModalOpen: boolean;
    setBlackKnightModalOpen: (next: boolean) => void;
    blackKnightModalData: any;
    opportunityID: any;
    oppValidationModalOpen: boolean;
    setOppValidationModalOpen: (next: boolean) => void;
    activeAIDesignId: string | null;
    activeSubScenarioId: string | null;
    savedAIDesigns: SavedAIDesign[];
    /** Stack financing grid above editor modals when tree cutting (or similar) is open. */
    pricingGridElevatedStack?: boolean;
    dashboardSectionPanel?: DashboardSectionPanelId;
    salesForceId?: string | null;
    commerceHubLoading?: boolean;
    onSolarPricingClick?: () => void;
    opportunityNumericId?: string | number | null;
}

export const DashboardOverlayExtrasSection: React.FC<DashboardOverlayExtrasSectionProps> = (props) => {
    const {
        initialDashboardReady,
        isDashboardOpen,
        onOpenAssistant,
        opportunityAssistantOpen,
        setOpportunityAssistantOpen,
        pricingGridModalOpen,
        setPricingGridModalOpen,
        theme,
        pricingFetchTrigger,
        pricingDesignForTrigger,
        priceQuote,
        setPricingFetchTrigger,
        ahjModalOpen,
        setAhjModalOpen,
        ahjTown,
        setbackNotes,
        addTitleContactsModalOpen,
        setAddTitleContactsModalOpen,
        titleSearchData,
        handleTitleSyncSuccess,
        blackKnightModalOpen,
        setBlackKnightModalOpen,
        blackKnightModalData,
        opportunityID,
        oppValidationModalOpen,
        setOppValidationModalOpen,
        activeAIDesignId,
        activeSubScenarioId,
        savedAIDesigns,
        pricingGridElevatedStack = false,
        dashboardSectionPanel = 'all',
        salesForceId,
        commerceHubLoading = false,
        onSolarPricingClick,
        opportunityNumericId,
    } = props;

    const pricingGridZIndex = pricingGridElevatedStack
        ? OBD_PRICING_GRID_NESTED_MODAL_Z
        : OBD_PRICING_GRID_MODAL_Z;

    return (
        <>
            <DashboardModalsSection
                initialDashboardReady={initialDashboardReady}
                isDashboardOpen={isDashboardOpen}
                opportunityAssistantOpen={opportunityAssistantOpen}
                setOpportunityAssistantOpen={setOpportunityAssistantOpen}
                pricingGridModalOpen={pricingGridModalOpen}
                setPricingGridModalOpen={setPricingGridModalOpen}
                theme={theme}
                pricingFetchTrigger={pricingFetchTrigger}
                pricingDesignForTrigger={pricingDesignForTrigger}
                priceQuote={priceQuote}
                setPricingFetchTrigger={setPricingFetchTrigger}
                ahjModalOpen={ahjModalOpen}
                setAhjModalOpen={setAhjModalOpen}
                ahjTown={ahjTown}
                setbackNotes={setbackNotes}
                addTitleContactsModalOpen={addTitleContactsModalOpen}
                setAddTitleContactsModalOpen={setAddTitleContactsModalOpen}
                titleSearchData={titleSearchData}
                handleTitleSyncSuccess={handleTitleSyncSuccess}
                blackKnightModalOpen={blackKnightModalOpen}
                setBlackKnightModalOpen={setBlackKnightModalOpen}
                blackKnightModalData={blackKnightModalData}
                opportunityID={opportunityID}
                oppValidationModalOpen={oppValidationModalOpen}
                setOppValidationModalOpen={setOppValidationModalOpen}
                pricingGridElevatedStack={pricingGridElevatedStack}
            />

            {initialDashboardReady ? (
                <CommerceHubModals
                    dashboardSectionPanel={dashboardSectionPanel}
                    salesForceId={salesForceId}
                    loading={commerceHubLoading}
                    onSolarPricingClick={onSolarPricingClick}
                    opportunityNumericId={opportunityNumericId}
                    priceQuote={priceQuote}
                    pricingGridModalOpen={pricingGridModalOpen}
                    pricingGridZIndex={pricingGridZIndex}
                />
            ) : null}

            <ActiveDesignBadge
                activeAIDesignId={activeAIDesignId}
                activeSubScenarioId={activeSubScenarioId}
                savedAIDesigns={savedAIDesigns}
                isDashboardOpen={isDashboardOpen}
            />

            {initialDashboardReady && isDashboardOpen && onOpenAssistant
                ? createPortal(
                      <GuideBotOverlay dock="bottom" onOpenAssistant={onOpenAssistant} />,
                      document.body
                  )
                : null}
        </>
    );
};
